import { LitElement, html, unsafeCSS, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import styles from './csv-import-cancel-confirm-body.css?inline';

import '@inmar/component-lib';

import type { CancelConfirmView } from '../../types/views';

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-cancel-confirm-body': CsvImportCancelConfirmBody;
  }
}

/**
 * Cancel confirmation body content for the inline cancel-confirmation panel.
 *
 * Renders the view-specific icon and message inside shadow DOM with encapsulated
 * styles. This component is slotted into the shell's `step-body` area by the
 * `cancelConfirmPanelTemplate` wrapper. Footer buttons live in the template
 * wrapper, not here.
 */
@customElement('csv-import-cancel-confirm-body')
export class CsvImportCancelConfirmBody extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties
  // ============================================================================

  /**
   * Which view variant to display.
   * - 'cancel-import': Standard cancel confirmation (warning icon + cancel message)
   * - 'download-report': Download report warning (warning icon + download message)
   * - 'download-report-success': Download succeeded (success icon + success message)
   */
  @property({ type: String, reflect: true })
  view: CancelConfirmView = 'cancel-import';

  // ============================================================================
  // View content
  // ============================================================================

  /**
   * Returns view-specific body content (icon + message) for the current view.
   */
  private _getViewContent(): TemplateResult {
    switch (this.view) {
      case 'cancel-import':
        return html`
          <hcds-icon
            name="diamond-exclamation"
            family="classic"
            icon-style="light"
            class="warning-icon"
            aria-hidden="true"
          ></hcds-icon>
          <p class="warning-message">
            Are you sure you want to cancel this import?
          </p>
        `;
      case 'download-report':
        return html`
          <hcds-icon
            name="diamond-exclamation"
            family="classic"
            icon-style="light"
            class="warning-icon"
            aria-hidden="true"
          ></hcds-icon>
          <p class="warning-message">
            Are you sure you want to leave without downloading your final error
            report?
          </p>
        `;
      case 'download-report-success':
        return html`
          <hcds-icon
            name="check-circle"
            family="classic"
            icon-style="light"
            class="success-icon"
            aria-hidden="true"
          ></hcds-icon>
          <p class="warning-message">
            Your error report has been downloaded. You can now leave this
            screen.
          </p>
        `;
    }
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render(): TemplateResult {
    return this._getViewContent();
  }
}
